import React, { useEffect, useId, useRef, useState } from 'react';
import Icon from './Icon';

/**
 * Small presentational primitives. Each renders the matching class from
 * styles/components.css and nothing else, so a page that prefers raw markup
 * (`<span className="badge badge-ok">`) gets the identical result.
 */

export function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

/* ── Badge ──────────────────────────────────────────────────────────────────
   tone: neutral | accent | ok | warn | danger | info | violet | outline */
export function Badge({ tone = 'neutral', dot, pulse, size, square, mono, icon, className, children, ...rest }) {
  return (
    <span
      className={cx('badge', `badge-${tone}`, size === 'lg' && 'badge-lg', square && 'badge-square',
        mono && 'badge-mono', className)}
      {...rest}
    >
      {dot && <span className={cx('badge-dot', pulse && 'pulse')} aria-hidden="true" />}
      {icon && <Icon name={icon} size={12} strokeWidth={2} />}
      {children != null && <span>{children}</span>}
    </span>
  );
}

/* ── StatusBadge ────────────────────────────────────────────────────────────
   An agent lifecycle state straight from the API. The class is the state
   string, so a state the stylesheet has never heard of still renders, in the
   neutral treatment, instead of disappearing. */
const STATUS_LABELS = {
  not_paired: 'Not paired',
  // An agent that has never run: workspaces are created at the user's first message.
  pending: 'Not started',
};

export function StatusBadge({ status, label, className }) {
  const s = (status == null || status === '') ? 'unknown' : String(status);
  const text = label || STATUS_LABELS[s] || s.replace(/_/g, ' ');
  return <span className={cx('status-badge', `status-${s}`, className)}>{text}</span>;
}

/* ── EmptyState ─────────────────────────────────────────────────────────── */
export function EmptyState({ icon = 'inbox', title, children, action, className }) {
  return (
    <div className={cx('empty-state', className)}>
      {icon && (
        <div className="empty-state-icon" aria-hidden="true">
          <Icon name={icon} size={22} />
        </div>
      )}
      {title && <div className="empty-state-title">{title}</div>}
      {children && <div className="empty-state-text">{children}</div>}
      {action && <div className="empty-state-action">{action}</div>}
    </div>
  );
}

/* ── Skeletons ──────────────────────────────────────────────────────────────
   Placeholders shaped like the content they stand in for, so the page does
   not jump when data lands. `aria-hidden` on the blocks and a single polite
   status on the wrapper: a screen reader hears "Loading" once, not 40 times. */
export function Skeleton({ width = '100%', height = 12, radius, className, style }) {
  return (
    <span
      className={cx('skeleton', className)}
      aria-hidden="true"
      style={{ width, height, borderRadius: radius, ...style }}
    />
  );
}

export function SkeletonText({ lines = 3, className }) {
  return (
    <div className={cx('stack', 'gap-2', className)} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={i === lines - 1 ? '62%' : '100%'} />
      ))}
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 5, label = 'Loading' }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">{label}</span>
      <div className="stack gap-0" aria-hidden="true">
        <div className="row gap-4" style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} width={c === 0 ? '18%' : `${10 + ((c * 7) % 9)}%`} height={10} />
          ))}
        </div>
        {Array.from({ length: rows }, (_, r) => (
          <div key={r} className="row gap-4" style={{ padding: '14px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
            {Array.from({ length: cols }, (_, c) => (
              <Skeleton key={c} width={c === 0 ? '22%' : `${8 + (((r + 2) * (c + 3)) % 12)}%`} height={12} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function CardSkeleton({ lines = 4, title = true }) {
  return (
    <div className="card" role="status" aria-busy="true">
      <span className="sr-only">Loading</span>
      {title && <Skeleton width="32%" height={14} style={{ marginBottom: 20 }} />}
      <SkeletonText lines={lines} />
    </div>
  );
}

/* ── SearchInput ────────────────────────────────────────────────────────── */
export function SearchInput({ value, onChange, placeholder = 'Search…', label, className,
                              autoFocus, inputRef, onKeyDown, style }) {
  return (
    <div className={cx('search-input', className)} style={style}>
      <Icon name="search" className="search-icon" />
      <input
        ref={inputRef}
        type="search"
        className="form-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        aria-label={label || placeholder}
        autoFocus={autoFocus}
        spellCheck={false}
        autoComplete="off"
      />
      {value ? (
        <button type="button" className="search-clear" aria-label="Clear search" onClick={() => onChange('')}>
          <Icon name="x" size={14} />
        </button>
      ) : null}
    </div>
  );
}

/* ── Pagination ─────────────────────────────────────────────────────────── */
function pageList(page, totalPages) {
  const pages = [];
  for (let p = 1; p <= totalPages; p += 1) {
    if (p === 1 || p === totalPages || Math.abs(p - page) <= 1) pages.push(p);
  }
  const out = [];
  pages.forEach((p, i) => {
    if (i > 0 && p - pages[i - 1] > 1) out.push(`gap-${p}`);
    out.push(p);
  });
  return out;
}

export function Pagination({ page, pageSize, total, onPage, noun = 'items' }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <nav className="pagination" aria-label="Pagination">
      <span className="num">
        {from}–{to} of {total} {noun}
      </span>
      <div className="pagination-pages">
        <button type="button" className="btn btn-ghost btn-sm btn-icon" disabled={page <= 1}
                onClick={() => onPage(page - 1)} aria-label="Previous page">
          <Icon name="chevronLeft" />
        </button>
        {pageList(page, totalPages).map((p) => (typeof p === 'string' ? (
          <span key={p} className="pagination-ellipsis" aria-hidden="true">…</span>
        ) : (
          <button key={p} type="button"
                  className={cx('btn btn-sm', p === page ? 'btn-secondary' : 'btn-ghost')}
                  aria-current={p === page ? 'page' : undefined}
                  onClick={() => onPage(p)}>
            {p}
          </button>
        )))}
        <button type="button" className="btn btn-ghost btn-sm btn-icon" disabled={page >= totalPages}
                onClick={() => onPage(page + 1)} aria-label="Next page">
          <Icon name="chevronRight" />
        </button>
      </div>
    </nav>
  );
}

/* ── Tabs ───────────────────────────────────────────────────────────────────
   WAI-ARIA tabs with automatic activation: arrow keys move AND select, Home
   and End jump. Only the selected tab is in the Tab order, so Tab leaves the
   strip instead of walking every tab.

   tabs: [{ value, label, count?, icon?, disabled? }]
   variant: 'segmented' (switch a view in place) | 'line' (page sections) */
// `idPrefix` (optional) gives each tab the id `${idPrefix}-tab-${value}` and points
// its aria-controls at `${idPrefix}-panel-${value}`, so a panel can be
// <div role="tabpanel" id={`${p}-panel-${v}`} aria-labelledby={`${p}-tab-${v}`}>.
export function Tabs({ tabs, value, onChange, variant = 'segmented', label, className, size, idPrefix }) {
  const refs = useRef([]);
  const enabled = tabs.filter((t) => !t.disabled);
  const onKeyDown = (e) => {
    const i = enabled.findIndex((t) => t.value === value);
    let next = null;
    if (e.key === 'ArrowRight') next = enabled[(i + 1) % enabled.length];
    else if (e.key === 'ArrowLeft') next = enabled[(i - 1 + enabled.length) % enabled.length];
    else if (e.key === 'Home') next = enabled[0];
    else if (e.key === 'End') next = enabled[enabled.length - 1];
    if (!next) return;
    e.preventDefault();
    onChange(next.value);
    const idx = tabs.findIndex((t) => t.value === next.value);
    refs.current[idx]?.focus();
  };
  return (
    <div role="tablist" aria-label={label}
         className={cx('tabs', variant === 'line' && 'tabs-line', size === 'sm' && 'tabs-sm', className)}
         onKeyDown={onKeyDown}>
      {tabs.map((t, i) => {
        const selected = t.value === value;
        // With nothing selected (a "mixed" state) the first enabled tab takes the
        // tab stop, or the whole strip would be unreachable from the keyboard.
        const tabStop = selected || (!tabs.some(x => x.value === value) && t === enabled[0]);
        return (
          <button
            key={t.value}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="tab"
            id={idPrefix ? `${idPrefix}-tab-${t.value}` : undefined}
            aria-controls={idPrefix ? `${idPrefix}-panel-${t.value}` : undefined}
            aria-selected={selected}
            tabIndex={tabStop ? 0 : -1}
            disabled={t.disabled}
            className={cx('tab', selected && 'tab-active')}
            onClick={() => onChange(t.value)}
          >
            {t.icon && <Icon name={t.icon} size={15} />}
            {t.label}
            {t.count != null && <span className="tab-count">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ── Switch ─────────────────────────────────────────────────────────────────
   Any `aria-*` prop is passed to the input, so a switch whose visible label lives
   elsewhere (a setting row) can still be named and described:
     <Switch id="x" aria-describedby="x-desc" … />  */
export function Switch({ checked, onChange, disabled, label, description, id, className, ...rest }) {
  const autoId = useId();
  const inputId = id || autoId;
  const descId = description ? `${inputId}-desc` : undefined;
  const aria = Object.fromEntries(Object.entries(rest).filter(([k]) => k.startsWith('aria-')));
  const control = (
    <span className="toggle-switch">
      <input
        id={inputId}
        type="checkbox"
        role="switch"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        aria-describedby={descId}
        {...aria}
      />
      <span className="toggle-slider" aria-hidden="true" />
    </span>
  );
  if (!label) return control;
  return (
    <label className={cx('toggle-label', className)} htmlFor={inputId}>
      {control}
      <span>
        <span className="setting-row-title">{label}</span>
        {description && <span id={descId} className="setting-row-desc" style={{ display: 'block' }}>{description}</span>}
      </span>
    </label>
  );
}

/* ── Field ──────────────────────────────────────────────────────────────────
   Label, control, then the error (when there is one) AND the hint: the hint is
   often what tells the reader how to fix the error, so it never disappears.

   When the child is a single element, it is wired up for assistive tech: the
   error and hint become its aria-describedby (merged with any it already has)
   and an error sets aria-invalid, unless the caller set those itself. */
export function Field({ label, htmlFor, hint, error, required, extra, className, children }) {
  const autoId = useId();
  const base = htmlFor || autoId;
  const errorId = error ? `${base}-error` : null;
  const hintId = hint ? `${base}-hint` : null;
  let control = children;
  if (React.isValidElement(children) && (errorId || hintId)) {
    const describedBy = [children.props['aria-describedby'], errorId, hintId].filter(Boolean).join(' ');
    control = React.cloneElement(children, {
      'aria-describedby': describedBy || undefined,
      'aria-invalid': children.props['aria-invalid'] ?? (error ? true : undefined),
    });
  }
  return (
    <div className={cx('form-group', className)}>
      {label && (
        <label className="form-label" htmlFor={htmlFor}>
          {label}
          {required && <span className="required" aria-hidden="true">*</span>}
          {extra}
        </label>
      )}
      {control}
      {error && <div className="form-error" id={errorId} role="alert">{error}</div>}
      {hint && <div className="form-hint" id={hintId}>{hint}</div>}
    </div>
  );
}

/* ── PageHeader ─────────────────────────────────────────────────────────────
   Layout only. The page passes its own <h1 className="page-title"> as the
   child, deliberately: the heading is part of the page, and the copy standard
   test counts exactly one <h1> per page file. */
export function PageHeader({ eyebrow, description, actions, meta, children }) {
  return (
    <div className="page-head-row">
      <div>
        {eyebrow && <div className="page-eyebrow">{eyebrow}</div>}
        {children}
        {description && <p className="page-lede">{description}</p>}
        {meta}
      </div>
      {actions && <div className="page-head-actions">{actions}</div>}
    </div>
  );
}

/* ── ProgressBar ────────────────────────────────────────────────────────── */
export function ProgressBar({ value, max = 1, tone, label, className }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (Number(value) / max) * 100)) : 0;
  const auto = tone || (pct >= 90 ? 'danger' : pct >= 75 ? 'warn' : undefined);
  return (
    <div className={cx('progress', className)} role="progressbar" aria-label={label}
         aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}>
      <div className={cx('progress-bar', auto)} style={{ width: `${pct}%` }} />
    </div>
  );
}

/* ── CopyButton ─────────────────────────────────────────────────────────── */
export function CopyButton({ value, label = 'Copy', className, size = 'sm' }) {
  const [done, setDone] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value ?? ''));
      setDone(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setDone(false), 1500);
    } catch (_) { /* clipboard refused: nothing useful to say */ }
  };
  return (
    <button type="button" onClick={copy} aria-label={done ? 'Copied' : label}
            className={cx('btn btn-ghost btn-icon', size === 'sm' && 'btn-sm', className)}>
      <Icon name={done ? 'check' : 'copy'} />
    </button>
  );
}

/* ── useClickOutside ────────────────────────────────────────────────────────
   The latest refs and callback are read through a ref, so callers can pass an
   inline array without re-subscribing the document listener on every render. */
export function useClickOutside(refs, onOutside, active = true) {
  const latest = useRef({ refs, onOutside });
  latest.current = { refs, onOutside };
  useEffect(() => {
    if (!active) return undefined;
    const handler = (e) => {
      const { refs: r, onOutside: cb } = latest.current;
      const list = Array.isArray(r) ? r : [r];
      if (list.some((x) => x && x.current && x.current.contains(e.target))) return;
      cb(e);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [active]);
}
